/**
 * 答えられなかった質問1件に、答えを書いて承認する画面（issue #109・docs/01 11-6）。
 *
 * 「未回答の質問の一覧から、答えの登録・承認まで画面を離れずに進められる」ための画面です。
 * 承認すると、その場で AI先輩が同じ質問に答えられるようになります。
 *
 * 質問は1件だけを読む窓口（`GET /admin/ai/questions/:id`）で読みます（issue #238）。
 * 一覧から探す作りだと、一覧の上限より古い質問で「見つからない」と事実と違う案内が出るためです。
 *
 * **すでに答え（承認待ち・使われているもの）がある質問では、書く欄を出しません**（issue #264）。
 * 新しく作ると同じ質問に答えが2つ並ぶので、その答えの画面へご案内します。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ApiError } from '../api/client.js';
import { useApi } from '../api/context.js';
import type { AiKnowledgeItem, AiUnansweredItem } from '../api/types.js';
import { formatDateTime } from '../members/labels.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure, nextStepFor } from '../api/load-failure.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: ReactNode;
}

export function AiQuestionDetailPage(): ReactNode {
  const api = useApi();
  const location = useLocation();
  const fromPending = (location.state as { from?: string } | null)?.from === '/ai/pending';
  const backTo = fromPending ? '/ai/pending' : '/ai/unanswered';
  const backLabel = fromPending ? '承認待ちの一覧に戻る' : '答えられなかった質問の一覧に戻る';
  const { id = '' } = useParams();
  const [item, setItem] = useState<AiUnansweredItem | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);
  /**
   * 作った下書きの番号。
   *
   * 承認は「下書きを作る → 承認する」の2回に分かれます。承認だけが失敗したときに
   * もう一度押すと、覚えていなければ下書きがもう1件できてしまうので、作った番号を
   * 覚えておき、二度目からは**同じ下書きを直してから**承認します。
   */
  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItem(null);
    setMissing(false);
    setError(null);
    api
      .get<AiUnansweredItem>(`/admin/ai/questions/${id}`)
      .then((found) => {
        if (cancelled) return;
        setItem(found);
        // 質問の欄は空で始める（issue #322）。質問した方の原文にはお客様のお名前・電話番号などが
        // 入っていることがあり、そのまま承認すると、送信取消や保存期間のあとも答えの側に残ってしまう
        // （決定28「写しを残さない」）。原文は上に参考として出すだけにする
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // 見当たらない（見てよい範囲の外・消えた）ときは、そのことを言葉でお伝えする
        if (caught instanceof ApiError && caught.status === 404) {
          setMissing(true);
          return;
        }
        setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, reloadKey]);

  async function approve(): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      // 下書きを作ってから承認する（作った時点ではまだ使われません・docs/01 11-6）。
      // もう一度押されたときは、前に作った下書きを直してから承認する（増やさない）
      let targetId = draftId;
      if (targetId === null) {
        const draft = await api.post<AiKnowledgeItem>('/admin/ai/knowledge', {
          body: {
            kind: 'qa',
            question,
            answer,
            source_ref: sourceRef === '' ? null : sourceRef,
            question_id: id,
          },
        });
        targetId = draft.id;
        setDraftId(draft.id);
      } else {
        await api.patch<AiKnowledgeItem>(`/admin/ai/knowledge/${targetId}`, {
          body: {
            question,
            answer,
            source_ref: sourceRef === '' ? null : sourceRef,
          },
        });
      }
      await api.post<AiKnowledgeItem>(`/admin/ai/knowledge/${targetId}/approve`);
      setResult({
        tone: 'success',
        message: '承認しました。AI先輩がこの質問に、出典つきで答えられるようになります。',
        nextStep: <Link to={`/ai/knowledge/${targetId}`}>登録した内容を見る</Link>,
      });
    } catch (caught) {
      const failure = loadFailure(caught);
      setResult({
        tone: 'error',
        message: withReason('承認できませんでした', failure.message),
        nextStep: nextStepFor(failure.status, failure.retryable).nextStep,
      });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  if (error) {
    return (
      <article>
        <PageHeader title="答えられなかった質問" />
        <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} />
        <p>
          <Link to={backTo}>{backLabel}</Link>
        </p>
      </article>
    );
  }
  if (missing) {
    return (
      <article>
        <PageHeader title="答えられなかった質問" />
        <ResultBanner
          tone="info"
          message="この質問は見つかりませんでした。"
          nextStep={`削除されたか、ご担当の範囲の外の質問です。${fromPending ? '承認待ち' : '答えられなかった質問'}の一覧からお探しください。`}
          action={
            <Link className="ui-button ui-button-secondary ui-button-compact" to={backTo}>
              {backLabel}
            </Link>
          }
        />
      </article>
    );
  }
  if (!item) {
    return (
      <article>
        <PageHeader title="答えられなかった質問" />
        <LoadingBlock label="この質問を読み込んでいます…" />
      </article>
    );
  }

  const done = result?.tone === 'success';
  // すでに答え（承認待ち・使われているもの）がある。ここで新しく作ると2つになるので、そちらへご案内する
  const existingDraftId = draftId === null ? (item.draft_id ?? null) : null;

  return (
    <article>
      <PageHeader
        title="答えられなかった質問"
        description="答えを書いて承認すると、次からAI先輩が同じ質問に答えられるようになります。"
        actions={
          <Link className="ui-button ui-button-secondary" to={backTo}>
            {backLabel}
          </Link>
        }
      />

      <Card title="聞かれた質問">
        <dl className="admin-facts">
          <dt>聞かれた日時</dt>
          <dd>{formatDateTime(item.created_at)}</dd>
        </dl>
        <p className="admin-ai-quote">{item.question}</p>
        <p className="admin-note">
          上は質問した方の原文です（参考）。この文は答えには残りません。下の「質問」の欄に、お名前や電話番号を入れずに、現場の方が聞きそうな言い方で書いてください。
        </p>
      </Card>

      {existingDraftId !== null ? (
        <Card title="答え">
          <ResultBanner
            tone="info"
            message="この質問には、答えがもうあります。"
            nextStep="新しく書くと答えが2つになるので、いまある答えを開いて直してください。"
            action={
              <Link
                className="ui-button ui-button-primary ui-button-compact"
                to={`/ai/knowledge/${existingDraftId}`}
              >
                いまある答えを開く
              </Link>
            }
          />
        </Card>
      ) : done ? (
        // 承認できたら、入力の欄はしまう。残しておくと二度押しで同じ内容がもう1件登録される
        <Card title="答えを書く">
          <ResultBanner {...result} />
        </Card>
      ) : (
        <FormCard
          title="答えを書く"
          description="承認するまで、AI先輩の答えには使われません。"
          submitLabel="承認する"
          submitBusyLabel="承認しています…"
          busy={busy}
          onSubmit={() => {
            // 空のまま送ると、サーバの言い方（「Q&A には質問が要ります」）で断られて分かりにくい
            if (question.trim() === '' || answer.trim() === '') {
              setResult({
                tone: 'error',
                message:
                  question.trim() === '' ? '質問を入力してください。' : '答えを入力してください。',
                nextStep: '質問と答えの両方を書いてから、「承認する」を押してください。',
              });
              return;
            }
            setResult(null);
            setConfirming(true);
          }}
          footer={result ? <ResultBanner {...result} /> : undefined}
        >
          <label className="ui-field" htmlFor="ai-question">
            <span>質問（どんな聞かれ方に答えるか。例：ドラム式洗濯機の固定は？）</span>
            <textarea
              id="ai-question"
              rows={2}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </label>
          <label className="ui-field" htmlFor="ai-answer">
            <span>答え</span>
            <textarea
              id="ai-answer"
              rows={6}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </label>
          <label className="ui-field" htmlFor="ai-source-ref">
            <span>出典（答えに添えてお返しします）</span>
            <input
              id="ai-source-ref"
              type="text"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
            />
          </label>
        </FormCard>
      )}

      <ConfirmDialog
        open={confirming}
        title="この答えを承認します"
        message={`承認すると、AI先輩がこの内容を根拠に、出典つきでお答えするようになります。質問「${question}」`}
        reversibility="承認したあと、答えの画面の「取り下げる」でいつでも使うのをやめられます。"
        reversible
        tone="primary"
        confirmLabel="承認する"
        busy={busy}
        onConfirm={() => void approve()}
        onCancel={() => setConfirming(false)}
      />
    </article>
  );
}
